import type { ReactNode } from 'react';
import type { DateRange } from 'react-day-picker';
import type { LucideIcon } from 'lucide-react';
import {
  CollectionEmptyState,
  CollectionErrorState,
  EntityListActiveDatePill,
  EntityListDateFilter,
  EntityListPagination,
  EntityListSkeletonRows,
  EntityListStateBlock,
  EntityListSortMenu,
  EntityListStatusDropdown,
  SearchInput,
  type EntityListSortOption,
} from '@/components/molecules';

interface EntitySplitListStatusFilter {
  value: string;
  options: Array<{ value: string; label: string }>;
  dotColors: Record<string, string>;
  allValue?: string;
  onChange: (value: string) => void;
}

interface EntitySplitListSort {
  activeIndex: number;
  activeLabel: string;
  options: EntityListSortOption[];
  onSelect: (index: number) => void;
  onReset: () => void;
}

interface EntitySplitListDateFilter {
  open: boolean;
  range: DateRange | undefined;
  onOpenChange: (open: boolean) => void;
  onSelect: (range: DateRange | undefined) => void;
  onClear: () => void;
}

interface EntitySplitListPagination {
  page: number;
  totalPages: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
}

interface EntitySplitListPanelProps {
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    loading?: boolean;
  };
  statusFilter?: EntitySplitListStatusFilter;
  sort?: EntitySplitListSort;
  dateFilter?: EntitySplitListDateFilter;
  toolbarEnd?: ReactNode;
  belowToolbar?: ReactNode;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  errorTitle?: string;
  emptyIcon: LucideIcon;
  emptyTitle: string;
  hasItems: boolean;
  skeletonCount?: number;
  children: ReactNode;
  pagination?: EntitySplitListPagination;
  footer?: ReactNode;
}

export function EntitySplitListPanel({
  search,
  statusFilter,
  sort,
  dateFilter,
  toolbarEnd,
  belowToolbar,
  isLoading,
  isError = false,
  onRetry,
  errorTitle,
  emptyIcon,
  emptyTitle,
  hasItems,
  skeletonCount = 8,
  children,
  pagination,
  footer,
}: EntitySplitListPanelProps) {
  const hasToolbar = !!(search || statusFilter || sort || dateFilter || toolbarEnd);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {hasToolbar ? (
        <div className="flex shrink-0 gap-1.5 border-b px-3 py-2">
          {search ? (
            <div className="min-w-0 flex-1">
              <SearchInput
                value={search.value}
                onChange={(event) => search.onChange(event.target.value)}
                placeholder={search.placeholder}
                loading={search.loading}
                className="h-8 text-sm"
              />
            </div>
          ) : null}

          {statusFilter ? (
            <EntityListStatusDropdown
              value={statusFilter.value}
              options={statusFilter.options}
              dotColors={statusFilter.dotColors}
              allValue={statusFilter.allValue}
              onChange={statusFilter.onChange}
            />
          ) : null}

          {sort ? (
            <EntityListSortMenu
              activeIndex={sort.activeIndex}
              activeLabel={sort.activeLabel}
              options={sort.options}
              onSelect={sort.onSelect}
              onReset={sort.onReset}
            />
          ) : null}

          {dateFilter ? (
            <EntityListDateFilter
              open={dateFilter.open}
              onOpenChange={dateFilter.onOpenChange}
              range={dateFilter.range}
              onSelect={dateFilter.onSelect}
              onClear={dateFilter.onClear}
            />
          ) : null}

          {toolbarEnd}
        </div>
      ) : null}

      {belowToolbar}

      {dateFilter ? (
        <EntityListActiveDatePill range={dateFilter.range} onClear={dateFilter.onClear} />
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isError ? (
          <EntityListStateBlock>
            <CollectionErrorState title={errorTitle} onRetry={onRetry} />
          </EntityListStateBlock>
        ) : null}

        {isLoading ? <EntityListSkeletonRows count={skeletonCount} /> : null}

        {!isLoading && !isError && !hasItems ? (
          <EntityListStateBlock>
            <CollectionEmptyState icon={emptyIcon} title={emptyTitle} />
          </EntityListStateBlock>
        ) : null}

        {!isLoading && !isError && hasItems ? <div className="pb-4 md:pb-0">{children}</div> : null}
      </div>

      {pagination ? (
        <EntityListPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          onPrevious={pagination.onPrevious}
          onNext={pagination.onNext}
        />
      ) : (
        footer
      )}
    </div>
  );
}
